import React, { useState } from 'react';
import { FaEye, FaEyeSlash, FaLock, FaUser } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import logo from '../assets/logo2.jpg';
import apiCall from '../helper/apiCall';
import { setUser } from '../store/userSlice';

const Login = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [data, setData] = useState({
        email: "",
        password: ""
    });

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleOnChange = (e) => {
        const { name, value } = e.target;
        setData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Using your apiCall template
            const response = await apiCall('loginUser', data);

            if (response.success) {
                toast.success(response.message || "Access Authorized");
                
                // Save admin data to global state
                dispatch(setUser(response.data));
                
                // Redirect to Dashboard
                navigate("/");
            } else {
                toast.error(response.message || "Invalid Credentials");
            }
        } catch (error) {
            console.error("Login Error:", error);
            toast.error("An error occurred. Please check your connection.");
        }
    };

    return (
        <section className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
            <div className="bg-white w-full max-w-md md:max-w-4xl rounded-sm shadow-2xl flex flex-col md:flex-row overflow-hidden border border-slate-100 animate-fadeIn">
                
                {/* Left Side: Branding */}
                <div className="hidden md:flex md:w-1/2 bg-slate-900 p-12 flex-col justify-between text-white">
                    <div>
                        <img src={logo} alt="Kanthasri" className="h-16 w-auto mb-6 brightness-200" />
                        <h2 className="text-3xl font-serif italic mb-4">Management Portal</h2>
                        <p className="text-slate-400 text-[10px] uppercase tracking-[0.3em] leading-relaxed">
                            Authorized personnel access only. Please verify your credentials to manage heritage collections.
                        </p>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        © 2026 Kanthasri Heritage
                    </div>
                </div>

                {/* Right Side: Form */}
                <div className="w-full md:w-1/2 p-8 lg:p-12">
                    <div className="mb-10 text-center md:text-left">
                        <h3 className="text-xl font-bold text-slate-800 uppercase tracking-widest">Admin Login</h3>
                        <p className="text-slate-400 text-[11px] mt-2 uppercase tracking-tighter">Enter secure credentials</p>
                    </div>

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        {/* Email */}
                        <div className="space-y-1">
                            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Email Address</label>
                            <div className="flex items-center border-b border-slate-200 focus-within:border-amber-600 transition-all py-2 group">
                                <FaUser className="text-slate-300 group-focus-within:text-amber-600 mr-3" size={14} />
                                <input 
                                    type="email" 
                                    name="email"
                                    value={data.email}
                                    onChange={handleOnChange}
                                    placeholder="Please enter your email"
                                    className="w-full outline-none text-sm placeholder:text-slate-200 bg-transparent"
                                    required
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div className="space-y-1">
                            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Secure Password</label>
                            <div className="flex items-center border-b border-slate-200 focus-within:border-amber-600 transition-all py-2 group">
                                <FaLock className="text-slate-300 group-focus-within:text-amber-600 mr-3" size={14} />
                                <input 
                                    type={showPassword ? "text" : "password"} 
                                    name="password"
                                    value={data.password}
                                    onChange={handleOnChange}
                                    placeholder="••••••••"
                                    className="w-full outline-none text-sm placeholder:text-slate-200 bg-transparent"
                                    required
                                />
                                <div 
                                    className="cursor-pointer text-slate-300 hover:text-slate-600 transition-colors"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                                </div>
                            </div>
                        </div>

                        {/* Submit */}
                        <button 
                            type="submit"
                            className="w-full bg-slate-900 text-white py-4 text-[11px] uppercase tracking-[0.3em] font-bold hover:bg-amber-700 transition-all shadow-lg active:scale-[0.98]"
                        >
                            Authorize Access
                        </button>
                    </form>

                    <div className="mt-10 pt-6 border-t border-slate-50 text-center">
                        <p className="text-[10px] text-slate-300 uppercase tracking-[0.2em]">
                            System Security Level: High
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Login;